import { h, Component } from 'preact'
import axios, { AxiosResponse } from 'axios'
import Header from './Header'
import Loader from '../ui-components/Loader'
import AlertComponent from '../ui-components/AlertComponent'
import Editor from '../editor'
import tracker from '../../components/tracker/publish'
import InputGroup from '../ui-components/InputGroup'
import Attachments from '../category/control/Attachments'
import Timepicker from '../timepicker/Timepicker'
import DatePicker from '../date-picker'
import TimezoneComponent, { TimezoneList, TimezoneSelector } from '../point-system/TimezoneSelector'
import DropdownControl from '../dropdown/DropdownControl'
import ButtonControl from '../button-control'
import globals from '../../utils/globals'
import FeaturedTopicsManager from '../featured-topics/FeaturedTopicsManager'
import { Group } from '../groups-destination/GroupOverview'
import RadioInputGroup from '../ui-components/RadioInputGroup'
import { common } from '../common'

const errorMessages = {
  titleEmpty: 'Please add a title',
  startDateEmpty: 'Please provide start date',
  endDateEmpty: 'Please provide start date',
  startTimeEmpty: 'Please provide start time',
  endTimeEmpty: 'Please provide end time',
  descriptionTooShort: 'Event description is too short',
  titleTooShort: 'Title must contain at least 3 characters',
  timezoneEmpty: 'Please select a time zone'
}

interface Time {
  hours?: string,
  minutes?: string,
  period?: string
}

interface FeaturedTopic {
  id: number,
  title: string,
  url: string,
  contentType: string
}

interface Type {
  id: number,
  name: string
}

interface Event {
  eventId: number,
  timezone: TimezoneSelector,
  featured_image?: string,
  type: string,
  title: string,
  start_date?: Date,
  end_date?: Date,
  location: string,
  url: string,
  urlLabel: string,
  description: string,
  open: boolean,
  featuredTopics: Array<FeaturedTopic>,
  confirmationMessage: string,
  group?: Group
}

interface Props {
  event?: Event,
  eventTypes: Array<Type>,
  timezoneList: TimezoneList,
  groups?: Array<Group>,
  csrfToken: string
}

class Errors {
  errors: Map<string, string>

  constructor () {
    this.errors = new Map()
  }

  isEmpty () : boolean {
    return this.errors.size === 0
  }

  addError (field: string, error: string) : this {
    this.errors.set(field, error)
    return this
  }

  clearError (field: string) : this {
    this.errors.delete(field)
    return this
  }

  getError (field: string) : string {
    return this.errors.get(field) || ''
  }
}

interface BackendResponse {
  messageType: string|null,
  messageText: string
}

interface State extends Event {
  start_time: Time,
  end_time: Time,
  isLoading: boolean,
  hasUnsavedChanges: boolean,
  showDeletedConfirmationModal: boolean,
  showPublishedModal: boolean,
  enableGroupsPicker: boolean,
  errors: Errors,
  backendResponse: BackendResponse
}

type ErrorResponse = AxiosResponse<{
  error?: string,
  redirect: string,
  errors: string[]
}>

interface AttachmentData {
  source_thumb?: string
}

class EventCreator extends Component<Props, State> {
  csrfToken: string
  private isPrivateGroupEnabled: boolean = common.isModuleActive('privateGroup')

  constructor (props: Props) {
    super(props)
    const event = props.event ? this.prepareEventState(props.event) : this.getDefaultEventState()
    this.state = {
      start_time: {},
      end_time: {},
      isLoading: false,
      hasUnsavedChanges: false,
      showDeletedConfirmationModal: false,
      enableGroupsPicker: false,
      showPublishedModal: false,
      errors: new Errors(),
      backendResponse: {
        messageType: null,
        messageText: ''
      },
      ...event
    }
    this.csrfToken = props.csrfToken
  }

  prepareEventState (event: Event) {
    return {
      ...event,
      featuredTopics: event.featuredTopics,
      start_date: new Date(event.start_date || ''),
      end_date: new Date(event.end_date || '')
    }
  }

  getDefaultEventState (): Event {
    return {
      eventId: 0,
      featured_image: '',
      type: '',
      title: '',
      location: '',
      url: '',
      urlLabel: '',
      description: '',
      open: true,
      timezone: {
        title: '',
        value: ''
      },
      featuredTopics: [],
      confirmationMessage: ''
    }
  }

  componentDidMount () {
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => this.onUnload(e))
  }

  componentWillUnmount () {
    window.removeEventListener('beforeunload', this.onUnload)
  }

  onUnload (e: BeforeUnloadEvent) {
    const { hasUnsavedChanges } = this.state

    if (hasUnsavedChanges) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  handlePublishResponse (res: AxiosResponse) {
    this.setState({ backendResponse: { messageType: 'success', messageText: 'This event has been published successfully!' } })
    const eventModerationPage = res.headers.location
    if (eventModerationPage) {
      const { title, type } = this.state
      const { userid, name, role } = window.inSidedData.user
      const communityUrl = globals('communityUrl')
      const eventId = res.data.id
      const eventDestinationUrl = communityUrl.concat('/events/', eventId)

      tracker.trackEvent('userevent', {
        'type': 'Event Created',
        'community_id': globals('communityId'),
        'destination_event_url': eventDestinationUrl,
        'author_id': userid,
        'author_role': role,
        'author_name': name,
        'event_id': eventId,
        'event_title': title,
        'event_type': type
      })
      window.location.assign(eventModerationPage)
    }
  }

  handlePostErrors (responseError: ErrorResponse) {
    if (responseError) {
      const errors = responseError.data.errors

      const errorsText = errors ? errors.join('\n') : 'Something went wrong'

      this.setState({ backendResponse: { messageType: 'error', messageText: errorsText } })
    }
  }

  hasErrors () {
    this.validateTitle()
    this.validateRequiredFields()

    return !this.state.errors.isEmpty()
  }

  validateRequiredFields () {
    const { start_date, end_date, start_time, end_time, errors, timezone, description } = this.state
    if (!start_date) {
      this.setState({ errors: errors.addError('start_date', errorMessages.startDateEmpty) })
    }
    if (!end_date) {
      this.setState({ errors: errors.addError('end_date', errorMessages.endDateEmpty) })
    }
    if (!timezone.value) {
      this.setState({ errors: errors.addError('timezone', errorMessages.timezoneEmpty) })
    }
    if (!start_time.minutes || !start_time.hours || !start_time.period) {
      this.setState({ errors: errors.addError('start_time', errorMessages.startTimeEmpty) })
    }
    if (!end_time.minutes || !end_time.hours || !end_time.period) {
      this.setState({ errors: errors.addError('end_time', errorMessages.endTimeEmpty) })
    }
    if (description.replace(/(<([^>]+)>)|(&.+;)|(\r\n|\n|\r)/igm, '').length < 3) {
      this.setState({ errors: errors.addError('description', errorMessages.descriptionTooShort) })
    }
  }

  validateTitle () {
    const { title, errors } = this.state
    if (!title) {
      this.setState({ errors: errors.addError('title', errorMessages.titleEmpty) })
    } else if (title.length < 3) {
      this.setState({ errors: errors.addError('title', errorMessages.titleTooShort) })
    }
  }

  async onPublish () {
    if (this.hasErrors()) {
      return
    }
    const { url, urlLabel, featured_image, eventId, end_date, timezone, end_time, location, title, start_time, start_date, type, featuredTopics, description, confirmationMessage, group } = this.state

    this.setState({ isLoading: true })

    axios.defaults.headers.common = {
      'X-CSRF-TOKEN': this.csrfToken
    }

    const endpoint = eventId !== 0 ? `/event/${eventId}` : '/content/publish/event'
    if (!start_date || !end_date || !timezone.value) {
      return
    }
    const data = {
      eventId: eventId,
      featured_image: featured_image,
      type: type,
      title: title,
      start_date: this.getFullUtcDate(start_date, start_time, timezone),
      end_date: this.getFullUtcDate(end_date, end_time, timezone),
      location: location,
      url: url,
      description: description,
      urlLabel: urlLabel,
      timezone: timezone.value,
      featuredTopics: featuredTopics,
      confirmationMessage: confirmationMessage,
      group: group ? group.id : null
    }

    try {
      const res = await axios.post(endpoint, data)
      if (res.status >= 400) {
        this.setState({
          isLoading: false,
          backendResponse: { messageType: 'error', messageText: 'Something went wrong, please try again later' }
        })
        return
      }
      this.setState({ hasUnsavedChanges: false, isLoading: false, backendResponse: { messageType: null, messageText: '' } })

      this.handlePublishResponse(res)
    } catch (error) {
      this.setState({ isLoading: false })
      const responseError = error.response || null
      this.handlePostErrors(responseError)
    }
  }

  getFullUtcDate (date: Date, time: Time, timezone: TimezoneSelector) {
    return new Date(date.toDateString() + ' ' + time.hours + ':' + time.minutes + ' ' +
      time.period + ' ' + timezone.offset).toISOString()
  }

  redirectToOverview (url: string) {
    this.setState({ hasUnsavedChanges: false })
    window.location.assign(`${url}?deleted&title=${this.state.title}`)
  }

  onDelete () {
  }

  setStartHours (hours: string | null) {
    let { start_time, errors } = this.state
    start_time.hours = hours || '-'
    this.setState({ start_time: start_time, errors: errors.clearError('start_time'), hasUnsavedChanges: true })
  }

  setStartPeriod (period: string | null) {
    let { start_time, errors } = this.state
    start_time.period = period || '-'
    this.setState({ start_time: start_time, errors: errors.clearError('start_time'), hasUnsavedChanges: true })
  }

  setStartMinutes (minutes: string | null) {
    let { start_time, errors } = this.state
    start_time.minutes = minutes || '-'
    this.setState({ start_time: start_time, errors: errors.clearError('start_time'), hasUnsavedChanges: true })
  }

  setEndHours (hours: string | null) {
    let { end_time, errors } = this.state
    end_time.hours = hours || '-'
    this.setState({ end_time: end_time, errors: errors.clearError('end_time'), hasUnsavedChanges: true })
  }

  setEndMinutes (minutes: string | null) {
    let { end_time, errors } = this.state
    end_time.minutes = minutes || '-'
    this.setState({ end_time: end_time, errors: errors.clearError('end_time'), hasUnsavedChanges: true })
  }

  setEndPeriod (period: string | null) {
    let { end_time, errors } = this.state
    end_time.period = period || '-'
    this.setState({ end_time: end_time, errors: errors.clearError('end_time'), hasUnsavedChanges: true })
  }

  setFeaturedTopics (key: string, value: Array<FeaturedTopic>) {
    this.setState({ featuredTopics: value, hasUnsavedChanges: true })
  }

  getTimeObject (time: string): Time {
    let m
    if ((m = /(\d+):(\d+)/.exec(time)) === null) return { minutes: '-', hours: '-', period: '-' }
    return { minutes: m[2], hours: m[1], period: '-' }
  }

  getTimezone (timezone: string) : TimezoneSelector {
    const find = this.props.timezoneList.find((tz: TimezoneSelector) => tz.value === timezone)
    if (!find) {
      this.setState({ errors: this.state.errors.addError('timezone', 'Failed to pick timezone') })
      return {
        title: '',
        value: ''
      }
    }
    return find
  }

  onPublishInChange (option: string) {
    this.setState({ enableGroupsPicker: option === 'Private group', group: undefined })
  }

  render ({ timezoneList, eventTypes, groups }: Props, {
    eventId,
    type,
    title,
    open,
    location,
    url,
    urlLabel,
    featured_image,
    description,
    isLoading,
    hasUnsavedChanges,
    errors,
    backendResponse,
    showDeletedConfirmationModal,
    showPublishedModal,
    start_date,
    start_time,
    end_date,
    end_time,
    timezone,
    confirmationMessage,
    enableGroupsPicker,
    group
  }: State) {
    return (
      <div className='qa-event-form'>
        <Header
          published={eventId !== 0}
          hasUnsavedChanges={hasUnsavedChanges}
          title={title}
          error={errors.getError('title')}
          csvDownloadUrl={`/event/csv?eventId=${eventId}`}
          csvDownloadText='Download attendee list'
          onTitleChanged={(title: string) => this.setState({
            title: title,
            hasUnsavedChanges: true,
            errors: errors.clearError('title')
          })}
          onPublish={() => this.onPublish()}
          onDeleteHandler={() => this.setState({ showDeletedConfirmationModal: true })}
        />
        {isLoading && <Loader />}
        <div className='control-body container'>
          {
            backendResponse.messageType !== null
              ? <AlertComponent messageType={backendResponse.messageType} message={backendResponse.messageText} sticky
                onClose={() => this.setState({ backendResponse: { messageType: null, messageText: '' } })} />
              : null
          }
          <div className='row event-type-wrapper'>
            <div className='col-xs-6'>
              <InputGroup title={'Event type'} optional customInput={
                <DropdownControl closeOnDropdownClick align='adjust' triggerClass={'type-dropdown qa-event-type'} items={eventTypes.map((type: Type) => ({
                  title: type.name,
                  onClick: () => this.setState({
                    type: type.name,
                    hasUnsavedChanges: true
                  })
                }))} trigger={
                  <ButtonControl class={type ? 'selected' : 'default'} title={type || 'Choose type'} icon={'arrowDropdown'} />
                } />
              } />
            </div>
            <div className='col-xs-6 link-manage-event-type'><a href='/settings/eventTypes' target='_blank'>Manage event types</a></div>
          </div>
          { groups && this.isPrivateGroupEnabled && <div className='row event-group-wrapper'>
            <div className='col-xs-6'>
              <RadioInputGroup title={'Publish in'} options={['Community', 'Private group']}
                valueDescriptions={['Discoverable by anyone.', 'Event will inherit visibility of group, e.g. an event in a private group is only discoverable by group members.']}
                onChange={option => this.onPublishInChange(option)} value={enableGroupsPicker ? 'Private group' : 'Community'} />
              <DropdownControl closeOnDropdownClick align='adjust' triggerClass={'group-dropdown qa-event-group'} disabled={!enableGroupsPicker} items={groups.map((group: Group) => ({
                title: group.title,
                onClick: () => this.setState({
                  group: group,
                  hasUnsavedChanges: true
                })
              }))} trigger={
                <ButtonControl disabled={!enableGroupsPicker} class={group ? 'selected' : 'default'} title={(group && group.title) || 'Choose group'} icon={'arrowDropdown'} />
              } />
            </div>
          </div> }
          <div className='row'>
            <div className='col-xs-6'>
              <InputGroup title={'Event timezone'} error={errors.getError('timezone')} customInput={<TimezoneComponent
                classes={['qa-event-timezone']}
                error={errors.getError('timezone') !== ''}
                timezones={timezoneList}
                selected={timezoneList.find((tz: TimezoneSelector) => tz.value === timezone.value)}
                onChange={(timezone: string) => this.setState({
                  timezone: this.getTimezone(timezone),
                  hasUnsavedChanges: true,
                  errors: errors.clearError('timezone')
                })} />}
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-xs-6'>
              <InputGroup title={'Start date'} error={errors.getError('start_date')}
                customInput={<DatePicker allowPastDates error={errors.getError('start_date') !== ''} value={start_date}
                  classes={['qa-event-start-date']} onChange={(dates: Date[]) => this.setState({
                    start_date: dates[0],
                    hasUnsavedChanges: true,
                    errors: errors.clearError('start_date')
                  })} />} />
            </div>
            <div className='col-xs-4'>
              <InputGroup title={'Start time'} error={errors.getError('start_time')}
                customInput={<Timepicker error={errors.getError('start_time') !== ''} hours={start_time.hours}
                  classes={['qa-event-start-time']}
                  minutes={start_time.minutes}
                  period={start_time.period}
                  onHoursChange={(h: string | null) => this.setStartHours(h)}
                  onMinutesChange={(m: string | null) => this.setStartMinutes(m)}
                  onPeriodChange={(p: string | null) => this.setStartPeriod(p)} />} />
            </div>
          </div>
          <div className='row'>
            <div className='col-xs-6'>
              <div>
                <InputGroup title={'End date'} error={errors.getError('end_date')}
                  customInput={<DatePicker allowPastDates error={errors.getError('end_date') !== ''} value={end_date}
                    classes={['qa-event-end-date']} onChange={(dates: Date[]) => this.setState({
                      end_date: dates[0],
                      errors: errors.clearError('end_date'),
                      hasUnsavedChanges: true
                    })} />} />
              </div>
            </div>
            <div className='col-xs-4'>
              <InputGroup title={'End time'} error={errors.getError('end_time')}
                customInput={<Timepicker error={errors.getError('end_time') !== ''} hours={end_time.hours}
                  classes={['qa-event-end-time']}
                  minutes={end_time.minutes}
                  period={end_time.period}
                  onHoursChange={(h: string | null) => this.setEndHours(h)}
                  onMinutesChange={(m: string | null) => this.setEndMinutes(m)}
                  onPeriodChange={(p: string | null) => this.setEndPeriod(p)} />} />
            </div>
          </div>
          <div className='row'>
            <div className='col-xs-12'>
              <InputGroup title='Location' value={location} qaClasses='qa-event-location' optional
                meta={<div>If you leave the location blank, your event will display as an <b>Online Event</b>. Otherwise, it will display as an <b>In-person Event</b>.</div>}
                onChange={(e: ChangeEvent) => this.setState({
                  location: e.target.value,
                  hasUnsavedChanges: true
                })} />
            </div>
          </div>
          <div className='row'>
            <div className='col-xs-6'>
              <InputGroup title={'Link URL'} value={url} qaClasses={'qa-event-url'} optional
                onChange={(e: ChangeEvent) => this.setState({
                  url: e.target.value,
                  hasUnsavedChanges: true
                })} />
            </div>
            <div className='col-xs-6'>
              <InputGroup title={'Link text'} value={urlLabel} qaClasses={'qa-event-url-label'} optional
                onChange={(e: ChangeEvent) => this.setState({
                  urlLabel: e.target.value,
                  hasUnsavedChanges: true
                })} />
            </div>
          </div>
          <div className='row'>
            <div className='col-xs-12'>
              <Attachments
                optional
                label='Event image'
                className='event-creator__sidebar-featured-image'
                meta={<div>Maximum file size: 5M. Allowed image types: .jpg, .jpeg, .png, .gif. We recommend a minimum
                  of 1200*600px</div>}
                isAttachmentSelected={featured_image && featured_image !== ''}
                imageSrc={featured_image || null}
                width={600}
                height={300}
                onUpdate={(data: AttachmentData) => this.setState({
                  featured_image: data ? data.source_thumb : undefined,
                  hasUnsavedChanges: true
                })}
              />
            </div>
          </div>
          <div className='row'>
            <div className='editor col-xs-12'>
              <InputGroup title={'Event description'} error={errors.getError('description')} customInput={<Editor
                classes={['qa-event-description']}
                error={errors.getError('description') !== ''}
                isControl
                data={description}
                onChange={(content: string) => {
                  if (content === description) return
                  this.setState({
                    description: content,
                    hasUnsavedChanges: true,
                    errors: errors.clearError('description')
                  })
                }} />} />
            </div>
          </div>
          <div className='row'>
            <div className='featured-topics col-xs-12'>
              <div className='form-group'>
                <label>Featured topics</label> <span>(optional)</span>
                <FeaturedTopicsManager
                  searchPlaceholder='Add a topic by pasting topic URL or searching'
                  onUpdate={(key, value) => this.setFeaturedTopics(key, value)}
                  featuredTopics={this.state.featuredTopics}
                  isSortable={false}
                  includeContentTypes={['discussion', 'question', 'article', 'ideation']}
                  description='Feature relevant topics that attendees can engage with before or after your event. These topics will be displayed on the event page.'
                />
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='editor-confirmation-message col-xs-12'>
              <div className='form-group'>
                <label>Confirmation message</label> <span>(optional)</span>
                <div className='confirmation-message-information'>This message will be shown when users RSVP to this event</div>
                <Editor
                  classes={['qa-event-confirmation-message']}
                  isControl
                  canHavePoll={false}
                  data={confirmationMessage}
                  onChange={(message: string) => {
                    if (message === confirmationMessage) return
                    this.setState({
                      confirmationMessage: message,
                      hasUnsavedChanges: true
                    })
                  }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default EventCreator
